import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { InviteModal } from '@/components/InviteModal';
import { useInviteToken } from '@/hooks/useInviteToken';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Share2 } from 'lucide-react';

export function ScheduledChallenge() {
  const { group, profile } = useAuth();
  const { t, language } = useLanguage();
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [countdown, setCountdown] = useState({ hours: 0, minutes: 0, seconds: 0 });
  const { token: inviteToken } = useInviteToken();

  // Calculate countdown to challenge start
  useEffect(() => {
    if (!group) return;

    const updateCountdown = () => {
      const now = new Date();
      const start = new Date(group.challenge_start);
      const diff = start.getTime() - now.getTime();

      if (diff <= 0) {
        // Challenge has started - trigger a refresh
        window.location.reload();
        return;
      }

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      setCountdown({ hours, minutes, seconds });
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [group]);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const options: Intl.DateTimeFormatOptions = {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    };
    const formatted = date.toLocaleDateString(language === 'sk' ? 'sk-SK' : 'en-GB', options);
    // Add Slovak preposition "V" at the beginning
    if (language === 'sk') {
      // Capitalize first letter of weekday
      const capitalized = formatted.charAt(0).toUpperCase() + formatted.slice(1);
      return `V ${capitalized.toLowerCase()}`;
    }
    return formatted;
  };

  const currentYear = new Date().getFullYear();

  // Display code: use invite token
  const displayCode = inviteToken || '···';

  if (!group) return null;

  return (
    <div className="min-h-screen-safe bg-background flex flex-col items-center justify-center p-6">
      {/* Invite Modal */}
      {showInviteModal && inviteToken && (
        <InviteModal 
          token={inviteToken} 
          onClose={() => setShowInviteModal(false)} 
        />
      )}

      <div className="w-full max-w-md space-y-6">
        {/* Header */}
        <div className="text-center">
          <div className="text-5xl mb-4">🕛</div>
          <p className="text-muted-foreground">
            {t('scheduledHi')} {profile?.user_id}!
          </p>
          <h1 className="text-2xl font-serif font-semibold text-foreground mt-2">
            {t('scheduledTitle')}
          </h1>
        </div>

        {/* Countdown Card */}
        <Card className="border-primary/20">
          <CardContent className="py-6">
            <p className="text-center text-sm text-muted-foreground mb-4">
              {t('scheduledCountdown')}
            </p>
            <div className="flex justify-center gap-4 text-center">
              <div>
                <div className="text-4xl font-bold font-mono text-primary">
                  {String(countdown.hours).padStart(2, '0')}
                </div>
                <div className="text-xs text-muted-foreground uppercase mt-1">
                  {t('scheduledHours')}
                </div>
              </div>
              <div className="text-4xl font-bold text-muted-foreground">:</div>
              <div>
                <div className="text-4xl font-bold font-mono text-primary">
                  {String(countdown.minutes).padStart(2, '0')}
                </div>
                <div className="text-xs text-muted-foreground uppercase mt-1">
                  {t('scheduledMinutes')}
                </div>
              </div>
              <div className="text-4xl font-bold text-muted-foreground">:</div>
              <div>
                <div className="text-4xl font-bold font-mono text-primary">
                  {String(countdown.seconds).padStart(2, '0')}
                </div>
                <div className="text-xs text-muted-foreground uppercase mt-1">
                  {t('scheduledSeconds')}
                </div>
              </div>
            </div>
            <p className="text-center text-sm text-muted-foreground mt-4">
              {formatDate(group.challenge_start)}
            </p>
          </CardContent>
        </Card>

        {/* Invite Section */}
        <Card>
          <CardContent className="py-6 space-y-4">
            <h2 className="text-lg font-semibold text-center">
              {t('scheduledInviteTitle')}
            </h2>
            <p className="text-sm text-muted-foreground text-center">
              {t('scheduledInviteDesc')}
            </p>
            
            {/* Invite Code Display */}
            <button
              onClick={() => inviteToken && setShowInviteModal(true)}
              className="w-full p-4 bg-muted/50 rounded-lg border border-border hover:border-primary/50 transition-colors"
            >
              <div className="text-3xl font-mono font-bold tracking-[0.3em] text-primary">
                {displayCode}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {t('scheduledTapToShare')}
              </p>
            </button>

            {/* Share Button */}
            <Button 
              onClick={() => inviteToken && setShowInviteModal(true)}
              className="w-full"
              size="lg"
              disabled={!inviteToken}
            >
              <Share2 className="h-5 w-5 mr-2" />
              {t('scheduledShareNow')}
            </Button>
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="text-center text-xs text-muted-foreground">
          {t('footerCopyright').replace('{year}', String(currentYear))}
        </p>
      </div>
    </div>
  );
}
